// Utility functions for color conversion and theme generation

/**
 * Convert HEX color to HSL format
 * @param hex - HEX color string (e.g., "#FF0099")
 * @returns HSL string in format "H S% L%" (e.g., "348 87% 91%")
 */
export function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse r, g, b values
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;
  
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (diff !== 0) {
    s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / diff) + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / diff + 2;
        break;
      case b:
        h = (r - g) / diff + 4;
        break;
    }
    h /= 6;
  }

  const hue = Math.round(h * 360);
  const saturation = Math.round(s * 100);
  const lightness = Math.round(l * 100);

  return `${hue} ${saturation}% ${lightness}%`;
}

/**
 * Calculate luminance of a color (for determining lightness)
 */
function getLuminance(hex: string): number {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  // Apply gamma correction
  const rLin = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLin = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLin = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
}

/**
 * Calculate contrast ratio between two colors
 */
function getContrastRatio(color1: string, color2: string): number {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  
  const bright = Math.max(lum1, lum2);
  const dark = Math.min(lum1, lum2);
  
  return (bright + 0.05) / (dark + 0.05);
}

/**
 * Map color palette to semantic tokens using deterministic strategy
 */
export function mapColorsToTokens(hexColors: string[], isDark: boolean = false): any {
  // Sort colors by luminance
  const colorsByLuminance = hexColors.map(hex => ({
    hex,
    luminance: getLuminance(hex),
    hsl: hexToHsl(hex)
  })).sort((a, b) => a.luminance - b.luminance);

  const darkest = colorsByLuminance[0];
  const lightest = colorsByLuminance[colorsByLuminance.length - 1];

  // Determine background and foreground based on theme type
  let background: string, foreground: string;
  if (isDark) {
    background = darkest.hsl;
    foreground = lightest.hsl;
  } else {
    background = lightest.hsl;
    foreground = darkest.hsl;
  }

  // Get remaining colors for accents (exclude bg/fg)
  const remainingColors = colorsByLuminance.filter(
    c => c.hex !== darkest.hex && c.hex !== lightest.hex
  );

  // Assign accent colors
  const primary = remainingColors[0]?.hsl || (isDark ? lightest.hsl : darkest.hsl);
  const secondary = remainingColors[1]?.hsl || primary;
  const accent = remainingColors[2]?.hsl || primary;

  // Calculate muted by averaging lightest and darkest
  const mutedLuminance = (darkest.luminance + lightest.luminance) / 2;
  const muted = remainingColors.find(c => 
    Math.abs(c.luminance - mutedLuminance) < 0.1
  )?.hsl || (isDark ? 
    hexToHsl('#' + Math.floor(mutedLuminance * 255).toString(16).repeat(3)) : 
    secondary
  );

  // Auto-generate foreground colors with proper contrast
  const primaryForeground = getContrastRatio(hexColors.find(h => hexToHsl(h) === primary) || '#000000', background === lightest.hsl ? lightest.hex : darkest.hex) >= 4.5 
    ? foreground 
    : (isDark ? lightest.hsl : darkest.hsl);

  const secondaryForeground = foreground;

  return {
    colors: {
      primary,
      'primary-foreground': primaryForeground,
      secondary,
      'secondary-foreground': secondaryForeground,
      background,
      foreground,
      muted,
      'muted-foreground': foreground,
      accent,
      'accent-foreground': foreground,
      destructive: '0 84.2% 60.2%',
      'destructive-foreground': background,
      border: isDark ? muted : secondary,
      input: isDark ? muted : secondary,
      ring: primary,
      card: background,
      'card-foreground': foreground,
      popover: background,
      'popover-foreground': foreground
    }
  };
}

// Predefined theme data
export const DARK_THEMES = [
  {
    name: 'Contraste Eterno',
    originalName: 'Black And White',
    hexColors: ['#F8F9FA', '#CFD1D4', '#A5A9AE', '#5F6266', '#181B1E']
  },
  {
    name: 'Lujo Nocturno',
    originalName: 'Black And Gold',
    hexColors: ['#0B0B0B', '#333533', '#F2ECDD', '#F5CB5C', '#CD9C20']
  },
  {
    name: 'Misterio Real',
    originalName: 'Purple And Black',
    hexColors: ['#0A0016', '#4A0754', '#800080', '#AB00C0', '#D500FF']
  },
  {
    name: 'Noche de Invierno',
    originalName: 'Black And Blue',
    hexColors: ['#0A6ED3', '#054E98', '#04356A', '#001D3F', '#000919']
  },
  {
    name: 'Bosque',
    originalName: 'Green And Black',
    hexColors: ['#0A1E1E', '#333F3F', '#DAFFDA', '#30B852', '#1F822E']
  },
  {
    name: 'Rebel',
    originalName: 'Pink And Black',
    hexColors: ['#382E31', '#191516', '#AC274F', '#EB638B', '#FFD9DA']
  },
  {
    name: 'Tierra Madre',
    originalName: 'Black And Brown',
    hexColors: ['#110703', '#230F08', '#34170D', '#6E3C19', '#A7795E']
  },
  {
    name: 'Serena Elegancia',
    originalName: 'Beige And Black',
    hexColors: ['#24201D', '#4F3E34', '#C6AC8F', '#DED1B6', '#F5F5DC']
  },
  {
    name: 'Crepúsculo',
    originalName: 'Purple, Black And White',
    hexColors: ['#0A020E', '#311440', '#572671', '#F1EDF3', '#A3D1CC']
  },
  {
    name: 'Minimalismo Clásico',
    originalName: 'Black And Cream',
    hexColors: ['#181600', '#3B3A30', '#7F7D67', '#E2E1C4', '#F3F3E0']
  },
  {
    name: 'Arena Nocturna',
    originalName: 'Black And Tan',
    hexColors: ['#000000', '#352D23', '#695A46', '#D2B48C', '#E9DAC6']
  },
  {
    name: 'Océano Profundo',
    originalName: 'Black And Teal',
    hexColors: ['#00B1B1', '#008080', '#114852', '#0B262B', '#040404']
  },
  {
    name: 'Sombras Urbanas',
    originalName: 'Black Aesthetic',
    hexColors: ['#000000', '#212020', '#90878E', '#C6C2C3', '#ECE8E5']
  }
];

export const LIGHT_THEMES = [
  {
    name: 'Al Natural',
    originalName: 'Light Pink',
    hexColors: ['#F6A6BB', '#F4BBC9', '#F7D1D8', '#FAE6E7', '#F7EEED']
  },
  {
    name: 'Algodón de Azúcar',
    originalName: 'Pastel Pink',
    hexColors: ['#FAE8ED', '#FDD5DF', '#FFC2D1', '#FFB2C5', '#FFA2B9']
  },
  {
    name: 'Dulce Canela',
    originalName: 'Pink And Brown',
    hexColors: ['#F790B2', '#F8B0C8', '#F9D0DE', '#AF7F73', '#733D26']
  },
  {
    name: 'Atardecer de Primavera',
    originalName: 'Pink And Purple',
    hexColors: ['#9F56FF', '#B582FF', '#CAADFF', '#FFADC7', '#FF99B6']
  },
  {
    name: 'Sueño Pastel',
    originalName: 'Pink And Blue',
    hexColors: ['#EB88E2', '#F7AEF8', '#5F77F2', '#8093F1', '#72DDF7']
  },
  {
    name: 'Coral Radiante',
    originalName: 'Pink And Orange',
    hexColors: ['#FC72A7', '#FCB0C6', '#FDC3DC', '#FFBD61', '#FF9954']
  },
  {
    name: 'Rosa Rubor',
    originalName: 'Blush Pink',
    hexColors: ['#C699A5', '#E49CB4', '#DBB4C4', '#EFCFE3', '#F0DDE8']
  },
  {
    name: 'Romance Vintage',
    originalName: 'Dusty Pink',
    hexColors: ['#B17596', '#D2899D', '#F29CA3', '#F5B3B8', '#F7CACD']
  },
  {
    name: 'Brisa',
    originalName: 'Pale Pink',
    hexColors: ['#F4BBC9', '#EFC9D0', '#E9D6D7', '#F0E2E2', '#F7EEED']
  },
  {
    name: 'Encanto',
    originalName: 'Pink Wedding',
    hexColors: ['#EDA7C1', '#FAC6DA', '#D1AFE4', '#9AC5ED', '#C3E3FF']
  }
];